import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { useFormatValue } from "../../../hooks/useFormatValue";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { withAuth } from "../../../components/HOC/withAuth";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";

import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Select from "../../../components/BaseUI/Select";
import Lozenge from "../../../components/BaseUI/Lozenge";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import { useTitle } from "../../../hooks/useTitle";
import {
  FiEdit3,
  FiTrash2,
  FiHome,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUsers,
  FiSettings,
  FiBell,
  FiPlus,
  FiEye,
} from "react-icons/fi";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const { user, getToken } = useAuth();
  const format = useFormatValue;
  const navigate = useNavigate();
  const canWrite =
    !!user &&
    hasPageWriteAccess(
      user.role,
      "/companies",
      user.pages || [],
      !!user.fullAccess,
    );
  useTitle("Rovion MES - Cariler");
  let headers = [
    { name: translate("UI.NAME"), dataType: "text", id: 0 },
    {
      name: translate("UI.COMPANY_CODE"),
      dataType: "text",
      id: 1,
      customSorter: (a, b) =>
        String(a.code || "").localeCompare(String(b.code || ""), undefined, {
          numeric: true,
          sensitivity: "base",
        }),
    },
    { name: translate("UI.EMAIL"), dataType: "text", id: 2 },
    { name: translate("UI.PHONE"), dataType: "text", id: 3 },
    { name: translate("UI.STATUS"), dataType: "option", id: 4 },
    { name: translate("UI.CREATED"), dataType: "date", id: 5 },
    { name: translate("UI.ACTIONS"), id: 6 },
  ];

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("company-list", headers);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: translate("UI.COMPANIES") },
  ];

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.data) {
        setCompanies(response.data || []);
      } else {
        setCompanies([]);
        setToasts([
          {
            description: response.data.message || "Error loading companies",
            id: Date.now(),
            title: "Error!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      setCompanies([]);
      setToasts([
        {
          description:
            error.response?.data?.message || "Error loading companies",
          id: Date.now(),
          title: "Error!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyInfo = async (company) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/companies/${company._id}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setCompanyInfo(response.data.company);
        setSidebarOpened(true);
      } else {
        setToasts([
          {
            description:
              response.data.message ||
              "Firma detayları yüklenirken bir hata oluştu.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Firma detayı getirme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Firma detayları yüklenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    }
  };

  const handleCompanyUpdate = async () => {
    try {
      setLoading(true);

      const response = await axios.put(
        `${API_URL}/api/companies/${companyInfo._id}`,
        companyInfo,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.success) {
        setToasts([
          {
            description: response.data.message,
            id: Date.now(),
            title: "Başarılı!",
            appearance: "success",
          },
        ]);

        await fetchCompanies();
        setSidebarOpened(false);
      } else {
        setToasts([
          {
            description:
              response.data.message || "Firma güncellenirken bir hata oluştu.",
            id: Date.now(),
            title: "Hata!",
            appearance: "danger",
          },
        ]);
      }
    } catch (error) {
      console.error("Firma güncelleme hatası:", error);
      setToasts([
        {
          description:
            error.response?.data?.message ||
            "Firma güncellenirken bir hata oluştu.",
          id: Date.now(),
          title: "Hata!",
          appearance: "danger",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const deleteCompany = (_id) => {
    const company = companies?.find((company) => company._id === _id);

    confirm({
      title: `${translate("UI.DELETE")} ${company?.name}?`,
      message:
        "Bu işlem geri alınamaz ve cari ile ilgili tüm veriler silinecektir.",
      appearance: "danger",
      confirmButtonText: translate("UI.DELETE"),
      onConfirm: async () => {
        try {
          const response = await axios.delete(
            `${API_URL}/api/companies/${_id}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            },
          );

          if (response.data.success) {
            await fetchCompanies();

            setToasts([
              {
                description: response.data.message,
                id: Date.now(),
                title: "Başarılı!",
                appearance: "success",
              },
            ]);
          }
        } catch (error) {
          console.error("Firma silme hatası:", error);
          setToasts([
            {
              description:
                error.response?.data?.message ||
                "Firma silinirken bir hata oluştu.",
              id: Date.now(),
              title: "Hata!",
              appearance: "danger",
            },
          ]);
        }
      },
    });
  };

  const removeToast = (index) => {
    const values = toasts;
    values.splice(index, 1);
    setToasts([...values]);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setCompanyInfo({});
  };

  const getStatusAppearance = (status) => {
    switch (status) {
      case "active":
        return "success-subtle";
      case "inactive":
        return "danger-subtle";
      default:
        return "neutral-subtle";
    }
  };

  const limitToThreeWords = (text) => {
    if (!text) return "-";
    return text.trim().split(/\s+/).slice(0, 3).join(" ");
  };

  return (
    <AppPage
      // title={translate("UI.COMPANIES")}
      breadcrumbs={breadcrumbs}
      loading={loading}
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="page-header-card p-3 d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-0">{translate("UI.COMPANIES")}</h2>
            </div>
            {canWrite && (
              <div>
                <Button
                  appearance="primary-animated"
                  onClick={() => navigate("/companies/create")}
                  icon={<FiPlus />}
                >
                  {translate("UI.ADD_COMPANY")}
                </Button>
              </div>
            )}
          </div>
        </ContainerItem>
        <ContainerItem sm={4} md={8} xl={12}>
          <Table
            columns={headers}
            visibleColumns={visibleColumns}
            tableId="company-list"
            checkboxSelection
            loading={loading}
            viewOptions={
              <ListViewOptionsPanel
                columns={headers}
                visibleColumns={visibleColumns}
                onToggleColumn={toggleColumnVisibility}
                onResetColumns={resetToDefault}
                showApply={false}
                showReset={false}
              />
            }
          >
            {companies.map((company, index) => (
              <Tr key={index} id={company?._id || index} data={company}>
                {visibleColumns.includes(0) && (
                  <Td
                    title={
                      typeof company.name === "string" && company.name.trim()
                        ? company.name
                        : undefined
                    }
                  >
                    {limitToThreeWords(company.name)}
                  </Td>
                )}
                {visibleColumns.includes(1) && (
                  <Td>
                    <code>{company.code}</code>
                  </Td>
                )}
                {visibleColumns.includes(2) && <Td>{company.email}</Td>}
                {visibleColumns.includes(3) && <Td>{company.phone}</Td>}
                {visibleColumns.includes(4) && (
                  <Td>
                    <Lozenge appearance={getStatusAppearance(company.status)}>
                      {translate(`UI.${company.status?.toUpperCase()}`)}
                    </Lozenge>
                  </Td>
                )}
                {visibleColumns.includes(5) && (
                  <Td>{format(company.createdAt, "date")}</Td>
                )}

                {visibleColumns.includes(6) && (
                  <Td>
                    <div className="d-flex align-items-center gap-1">
                      <IconButton
                        icon={<FiEye style={{ fontSize: "18px" }} />}
                        appearance="subtle"
                        shape="circle"
                        onClick={() => navigate(`/companies/${company._id}`)}
                      />
                      {canWrite && (
                        <>
                          <IconButton
                            icon={<FiEdit3 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            onClick={() => handleCompanyInfo(company)}
                          />
                          <IconButton
                            icon={<FiTrash2 style={{ fontSize: "18px" }} />}
                            appearance="subtle"
                            shape="circle"
                            onClick={() => deleteCompany(company._id)}
                          />
                        </>
                      )}
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </Table>
        </ContainerItem>
      </Container>

      <SidebarContainer
        show={sidebarOpened}
        title={translate("UI.COMPANY_DETAILS")}
        onClose={closeSidebar}
      >
        <div className="d-flex align-items-center mb-4">
          <div className="me-3">
            <div className="avatar avatar-md bg-primary-subtle rounded">
              <FiUsers
                className="text-primary"
                style={{ width: "24px", height: "24px" }}
              />
            </div>
          </div>
          <div>
            <h5 className="card-title mb-1">
              {translate("UI.COMPANY_INFORMATION")}
            </h5>
            <p className="text-muted small mb-0">
              {translate("UI.COMPANY_INFO_DESC")}
            </p>
          </div>
        </div>
        <Input
          type="text"
          value={companyInfo?.name}
          label={translate("UI.NAME")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, name: value }))
          }
          prefix={<FiUsers className="text-muted" />}
          required
        />
        <Input
          type="text"
          value={companyInfo?.code}
          label={translate("UI.COMPANY_CODE")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, code: value.toUpperCase() }))
          }
          hint={translate("UI.COMPANY_CODE_DESC")}
          required
        />
        <Input
          type="email"
          value={companyInfo?.email}
          label="Email"
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, email: value }))
          }
          prefix={<FiMail className="text-muted" />}
          required
        />
        <Input
          type="tel"
          value={companyInfo?.phone}
          label={translate("UI.COMPANY_PHONE")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, phone: value }))
          }
          prefix={<FiPhone className="text-muted" />}
          hint={translate("UI.COMPANY_PHONE_DESC")}
          required
        />
        <Input
          type="text"
          value={companyInfo?.address}
          label={translate("UI.ADDRESS")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, address: value }))
          }
          prefix={<FiMapPin className="text-muted" />}
          required
        />
        <Input
          type="text"
          value={companyInfo?.taxNumber}
          label={translate("UI.TAX_NUMBER")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, taxNumber: value }))
          }
          prefix={<FiMapPin className="text-muted" />}
          hint={translate("UI.TAX_NUMBER_DESC")}
        />
        <Input
          type="text"
          value={companyInfo?.taxOffice}
          label={translate("UI.TAX_OFFICE")}
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, taxOffice: value }))
          }
          prefix={<FiMapPin className="text-muted" />}
          hint={translate("UI.TAX_OFFICE_DESC")}
        />
        <Input
          type="text"
          value={companyInfo?.yetkiliKisi || ""}
          label="Yetkili Kişi"
          onChange={(value) =>
            setCompanyInfo((prev) => ({ ...prev, yetkiliKisi: value }))
          }
          prefix={<FiUsers className="text-muted" />}
        />
        <div className="mt-4">
          <Button
            title={translate("UI.SAVE")}
            appearance="primary-animated"
            onClick={handleCompanyUpdate}
            loading={loading}
          />
        </div>
      </SidebarContainer>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            {...toast}
            key={index}
            // actions={[{ content: "Kapat", onClick: () => removeToast(index) }]}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(Companies, ["admin", "employee", "user"]);
